import React, { useState, useMemo } from 'react';
import { uploadFile } from '../../api/files';
import './styles/BackgroundEditor.css';

export default function BackgroundEditor({ data, onUpdate }) {
  const cfg = { ...(data || {}) };
  const [uploading, setUploading] = useState(false);
  
  // 确定当前背景类型
  const backgroundType = useMemo(() => {
    if (cfg.image) return 'image';
    if (cfg.gradient?.enabled) return 'gradient';
    return 'solid';
  }, [cfg.image, cfg.gradient?.enabled]);
  
  const [activeTab, setActiveTab] = useState(backgroundType);

  const update = (patch) => onUpdate({ ...cfg, ...patch });

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setUploading(true);
      const result = await uploadFile(file);
      const url = result?.fileUrl || result?.url || result?.data?.fileUrl || result?.data?.url;
      if (url) {
        update({ image: url });
        setActiveTab('image');
      }
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteImage = () => {
    update({ image: null });
    if (activeTab === 'image') {
      setActiveTab('solid');
    }
  };

  const handleTabChange = (type) => {
    setActiveTab(type);
    if (type === 'gradient') {
      update({ gradient: { ...(cfg.gradient || {}), enabled: true } });
    } else if (type === 'solid') {
      update({ gradient: { ...(cfg.gradient || {}), enabled: false } });
    }
  };

  // 计算预览样式
  const previewStyle = useMemo(() => {
    const style = {};
    
    if (activeTab === 'gradient' && cfg.gradient?.enabled) {
      const angle = cfg.gradient?.angle || 180;
      const from = cfg.gradient?.from || '#ffffff';
      const to = cfg.gradient?.to || '#ffffff';
      style.background = `linear-gradient(${angle}deg, ${from}, ${to})`;
    } else if (activeTab === 'image' && cfg.image) {
      style.backgroundImage = `url(${cfg.image})`;
      style.backgroundSize = cfg.size || 'cover';
      style.backgroundPosition = cfg.position || 'center';
      style.backgroundRepeat = cfg.repeat || 'no-repeat';
    } else {
      style.backgroundColor = cfg.color || '#ffffff';
    }
    
    return style;
  }, [activeTab, cfg]);

  // 遮罩样式
  const overlayStyle = useMemo(() => {
    if (!cfg.overlayOpacity || cfg.overlayOpacity === 0) return null;
    return {
      backgroundColor: cfg.overlayColor || '#000000',
      opacity: cfg.overlayOpacity
    };
  }, [cfg.overlayOpacity, cfg.overlayColor]);

  return (
    <div className="background-editor">
      <h3 className="be-title">背景设置</h3>
      
      {/* Tab切换 */}
      <div className="be-tabs">
        <button
          className={`be-tab ${activeTab === 'solid' ? 'active' : ''}`}
          onClick={() => handleTabChange('solid')}
        >
          纯色
        </button>
        <button
          className={`be-tab ${activeTab === 'gradient' ? 'active' : ''}`}
          onClick={() => handleTabChange('gradient')}
        >
          渐变
        </button>
        <button
          className={`be-tab ${activeTab === 'image' ? 'active' : ''}`}
          onClick={() => handleTabChange('image')}
        >
          图片
        </button>
      </div>

      {/* 预览区域 */}
      <div className="be-preview-section">
        <div className="be-preview-label">预览</div>
        <div className="be-preview-box" style={previewStyle}>
          {overlayStyle && (
            <div className="be-preview-overlay" style={overlayStyle} />
          )}
          {activeTab === 'image' && !cfg.image && (
            <div className="be-preview-placeholder">暂无图片</div>
          )}
        </div>
      </div>

      {/* 纯色配置 */}
      {activeTab === 'solid' && (
        <div className="be-section">
          <label className="be-label">
            <span>背景颜色</span>
            <div className="be-color-input-wrapper">
              <input
                type="color"
                className="be-color-input"
                value={cfg.color || '#ffffff'}
                onChange={(e) => update({ color: e.target.value })}
              />
              <span className="be-color-value">{cfg.color || '#ffffff'}</span>
            </div>
          </label>
        </div>
      )}

      {/* 渐变配置 */}
      {activeTab === 'gradient' && (
        <div className="be-section">
          <div className="be-gradient-controls">
            <label className="be-label">
              <span>起始颜色</span>
              <div className="be-color-input-wrapper">
                <input
                  type="color"
                  className="be-color-input"
                  value={cfg.gradient?.from || '#ffffff'}
                  onChange={(e) => update({ gradient: { ...(cfg.gradient || {}), from: e.target.value } })}
                />
                <span className="be-color-value">{cfg.gradient?.from || '#ffffff'}</span>
              </div>
            </label>
            
            <label className="be-label">
              <span>结束颜色</span>
              <div className="be-color-input-wrapper">
                <input
                  type="color"
                  className="be-color-input"
                  value={cfg.gradient?.to || '#ffffff'}
                  onChange={(e) => update({ gradient: { ...(cfg.gradient || {}), to: e.target.value } })}
                />
                <span className="be-color-value">{cfg.gradient?.to || '#ffffff'}</span>
              </div>
            </label>
          </div>

          <label className="be-label">
            <span>渐变角度</span>
            <div className="be-slider-wrapper">
              <input
                type="range"
                className="be-slider"
                min="0"
                max="360"
                value={cfg.gradient?.angle ?? 180}
                onChange={(e) => update({ gradient: { ...(cfg.gradient || {}), angle: Number(e.target.value) } })}
              />
              <span className="be-slider-value">{cfg.gradient?.angle ?? 180}°</span>
            </div>
          </label>
        </div>
      )}

      {/* 图片配置 */}
      {activeTab === 'image' && (
        <div className="be-section">
          <label className="be-label">
            <span>背景图片</span>
            <div className="be-image-upload">
              <input
                type="file"
                id="bg-image-upload"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploading}
                style={{ display: 'none' }}
              />
              {cfg.image ? (
                <div className="be-image-preview-wrapper">
                  <div className="be-image-preview">
                    <img src={cfg.image} alt="背景预览" />
                    <button
                      className="be-image-delete"
                      onClick={handleDeleteImage}
                      title="删除图片"
                    >
                      ×
                    </button>
                  </div>
                  <div className="be-image-info">
                    <span className="be-image-url">{cfg.image}</span>
                  </div>
                </div>
              ) : (
                <label htmlFor="bg-image-upload" className="be-image-upload-btn" style={{ cursor: uploading ? 'not-allowed' : 'pointer' }}>
                  {uploading ? '上传中...' : '选择文件'}
                </label>
              )}
            </div>
          </label>

          <div className="be-image-layout">
            <label className="be-label">
              <span>图片大小</span>
              <select
                className="be-select"
                value={cfg.size || 'cover'}
                onChange={(e) => update({ size: e.target.value })}
              >
                <option value="cover">覆盖</option>
                <option value="contain">包含</option>
                <option value="auto">自动</option>
                <option value="100% 100%">100% × 100%</option>
              </select>
            </label>

            <label className="be-label">
              <span>图片位置</span>
              <select
                className="be-select"
                value={cfg.position || 'center'}
                onChange={(e) => update({ position: e.target.value })}
              >
                <option value="center">居中</option>
                <option value="top">顶部</option>
                <option value="bottom">底部</option>
                <option value="left">左侧</option>
                <option value="right">右侧</option>
              </select>
            </label>

            <label className="be-label">
              <span>重复方式</span>
              <select
                className="be-select"
                value={cfg.repeat || 'no-repeat'}
                onChange={(e) => update({ repeat: e.target.value })}
              >
                <option value="no-repeat">不重复</option>
                <option value="repeat">重复</option>
                <option value="repeat-x">横向重复</option>
                <option value="repeat-y">纵向重复</option>
              </select>
            </label>
          </div>
        </div>
      )}

      {/* 遮罩配置（所有类型都可用） */}
      <div className="be-section be-section-divider">
        <label className="be-label">
          <span>遮罩效果</span>
          <div className="be-overlay-controls">
            <div className="be-color-input-wrapper">
              <input
                type="color"
                className="be-color-input"
                value={cfg.overlayColor || '#000000'}
                onChange={(e) => update({ overlayColor: e.target.value })}
              />
              <span className="be-color-label">遮罩颜色</span>
            </div>
            <div className="be-slider-wrapper">
              <span className="be-slider-label">透明度</span>
              <input
                type="range"
                className="be-slider"
                min="0"
                max="100"
                step="5"
                value={(cfg.overlayOpacity ?? 0) * 100}
                onChange={(e) => update({ overlayOpacity: Number(e.target.value) / 100 })}
              />
              <span className="be-slider-value">{Math.round((cfg.overlayOpacity ?? 0) * 100)}%</span>
            </div>
          </div>
        </label>
      </div>
    </div>
  );
}
